import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  FileSignature, Upload, Users, Send, CheckCircle, Clock,
  AlertCircle, FileText, X, Plus, Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESignatureWorkflow = ({ isOpen, onClose, documentData, agreementType = 'novation' }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Recipients, 2: Review, 3: Sent
  const [recipients, setRecipients] = useState([
    { email: '', name: '', role: 'signer' }
  ]);
  const [signatureRequestId, setSignatureRequestId] = useState(null);

  const addRecipient = () => {
    setRecipients([...recipients, { email: '', name: '', role: 'signer' }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const validateRecipients = () => {
    for (const r of recipients) {
      if (!r.email || !r.name) {
        toast.error('Please fill in all recipient details');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email)) {
        toast.error(`Invalid email: ${r.email}`);
        return false;
      }
    }
    return true;
  };

  const handleSendForSignature = async () => {
    if (!validateRecipients()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/esignature-workflow/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          agreement_type: agreementType,
          agreement_id: documentData?.agreement_id || documentData?.order_id,
          document_name: documentData?.document_name || `${agreementType}_agreement.pdf`,
          recipients: recipients,
          sender_name: user?.name || user?.email,
          sender_email: user?.email
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSignatureRequestId(data.request_id);
        setStep(3);
        toast.success('Document sent for signature!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send document');
      }
    } catch (error) {
      console.error('E-signature error:', error);
      toast.error('Failed to initiate signature request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <Card className="bg-[#0d1d35] border-[#1a2d4a] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-[#1a2d4a]">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-[#b8860b]" />
                Electronic Signature Request
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1">
                Send document for electronic signature
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-[#b8860b] text-white' : 'bg-[#1a2d4a] text-gray-500'
                }`}>
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-1 ${step > s ? 'bg-[#b8860b]' : 'bg-[#1a2d4a]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Add Recipients</span>
            <span>Review</span>
            <span>Sent</span>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          {step === 1 && (
            <div className="space-y-6">
              {/* Document Info */}
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-[#b8860b]" />
                  <div>
                    <p className="text-white font-medium">
                      {documentData?.document_name || `${agreementType.replace('_', ' ')} Agreement`}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {documentData?.agreement_id || documentData?.order_id || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Recipients */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm text-[#b8860b]">
                    <Users className="w-4 h-4 inline mr-1" />
                    Signers
                  </label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addRecipient}
                    className="border-[#b8860b] text-[#b8860b]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Signer
                  </Button>
                </div>
                
                {recipients.map((recipient, index) => (
                  <div key={index} className="mb-4 p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-gray-400">Signer {index + 1}</span>
                      {recipients.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeRecipient(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="text"
                        placeholder="Full Name"
                        value={recipient.name}
                        onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                      />
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={recipient.email}
                        onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                      />
                    </div>
                    <select
                      value={recipient.role}
                      onChange={(e) => updateRecipient(index, 'role', e.target.value)}
                      className="mt-3 w-full px-3 py-2 bg-[#1a2d4a] border border-[#2a3d5a] rounded-md text-white text-sm"
                    >
                      <option value="signer">Signer</option>
                      <option value="cc">CC (Copy Only)</option>
                      <option value="witness">Witness</option>
                    </select>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  className="flex-1 border-[#1a2d4a] text-gray-400"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => setStep(2)}
                  className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                  disabled={!recipients.some(r => r.email && r.name)}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-6">
              <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                <h3 className="text-sm text-[#b8860b] mb-4">Review Signature Request</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500">Document</p>
                    <p className="text-white">{documentData?.document_name || `${agreementType.replace('_', ' ')} Agreement`}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">Agreement Type</p>
                    <p className="text-white capitalize">{agreementType.replace('_', ' ')}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Recipients ({recipients.length})</p>
                    {recipients.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2 p-2 bg-[#1a2d4a] rounded">
                        <div className="w-6 h-6 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                          <span className="text-xs text-[#b8860b]">{i + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm">{r.name}</p>
                          <p className="text-xs text-gray-500">{r.email}</p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400 capitalize">
                          {r.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Recipients will receive an email with a link to sign the document electronically.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1 border-[#1a2d4a] text-gray-400"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSendForSignature}
                  disabled={loading}
                  className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                >
                  {loading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send for Signature
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl text-white mb-2">Document Sent!</h3>
              <p className="text-gray-400 mb-6">
                Your document has been sent to {recipients.length} recipient(s) for signature.
              </p>
              
              {signatureRequestId && (
                <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a] mb-6">
                  <p className="text-sm text-gray-500">Signature Request ID</p>
                  <p className="text-[#b8860b] font-mono">{signatureRequestId}</p>
                </div>
              )}
              
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-6">
                <p className="text-blue-400 text-sm flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  You'll be notified when all parties have signed.
                </p>
              </div>
              
              <Button onClick={onClose} className="bg-[#b8860b] hover:bg-[#9a7209]">
                Close
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ESignatureWorkflow;
